// ** React Imports
import { useRef } from 'react'

// ** Third Party Imports
import ContentLoader from 'react-content-loader'
import { useOnClickOutside } from 'usehooks-ts'

// ** Component Imports
import { Avatar, Typography } from '@/shared/ui'

// ** Store & Actions
import { setIsSidebarOpen } from '@/entities/layout'
import { useAppDispatch, useAppSelector } from '@/shared/lib/hooks'

// ** Feature Imports
import { LogoutButton } from '@/features/auth/logout'
import { CloseSidebarIcon } from '@/features/layout/toggle'

// ** Action Imports
import { useGetCurrentUserQuery } from '@/entities/user'

// ** Utility Imports
import { env, getFullName, getInitials } from '@/shared/lib'
import { isSuperAdmin } from '@/shared/lib/rbac'

// ** Icon Imports
import { Chat, SubscriptionPlan } from '@/shared/ui/icons'

// ** Styled Components
import {
	AnalyticsIcon,
	ClientDashboardIcon,
	ClientManagementIcon,
	CreateNewWidget,
	DashboardIcon,
	EmptyContainer,
	Header,
	HelpIcon,
	PriceManagementIcon,
	ResourcesIcon,
	Separator,
	SidebarContent,
	SidebarExternalLink,
	SidebarLink,
	SidebarLinks,
	SidebarProfileLink,
	SidebarTopText,
	SidebarWrapper,
	TaxStrategiesIcon
} from './styles'

export const Sidebar = () => {
	// ** Hooks
	const dispatch = useAppDispatch()
	const sidebarRef = useRef<HTMLDivElement>(null)
	const isSidebarOpen = useAppSelector((state) => state.layout.isSidebarOpen)
	// queries
	const { data: userData, isFetching } = useGetCurrentUserQuery()

	// ** Variables
	// user data
	const photoKey = userData?.photoKey ?? ''
	const photoUrl =
		photoKey !== null && photoKey.length > 0
			? `${env.cloudFrontUrl}/${photoKey}`
			: null
	const fullName = getFullName(userData?.firstName, userData?.lastName)
	const isSuperAdminUser = isSuperAdmin(userData)

	useOnClickOutside(sidebarRef, () => dispatch(setIsSidebarOpen(false)))

	const closeSidebar = () => dispatch(setIsSidebarOpen(false))

	return (
		<SidebarWrapper>
			<SidebarContent $isSidebarOpen={isSidebarOpen} ref={sidebarRef}>
				<Header>
					<CloseSidebarIcon />
				</Header>
				{/* Sidebar user info */}
				<SidebarProfileLink to='/user-profile?tab=edit' onClick={closeSidebar}>
					{isFetching ? (
						<ContentLoader
							width={216}
							speed={1.5}
							height={40}
							opacity={0.5}
							viewBox='0 0 216 40'
							interval={0.2}
							backgroundColor='#fafafa'
							foregroundColor='rgba(171,212,210,0.6)'
						>
							<circle cx='20' cy='20' r='20' />
							<rect x='48' y='8' rx='3' ry='3' width='88' height='6' />
							<rect x='48' y='26' rx='3' ry='3' width='52' height='6' />
						</ContentLoader>
					) : (
						<>
							<Avatar
								src={photoUrl ?? undefined}
								width='2.5rem'
								height='2.5rem'
								fallbackText={getInitials(fullName)}
							/>
							<SidebarTopText>
								<Typography variant='body1' textTrunc={1}>
									{fullName}
								</Typography>
								<Typography variant='body3' textTrunc={1}>
									{userData?.email}
								</Typography>
							</SidebarTopText>
						</>
					)}
				</SidebarProfileLink>

				<Separator />

				{/* Sidebar links */}
				<SidebarLinks>
					{isSuperAdminUser ? (
						<>
							{/* Admin-only links */}
							<SidebarLink onClick={closeSidebar} to='/admin/dashboard'>
								<DashboardIcon />
								<Typography variant='body1'>Dashboard</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/admin/user-database'>
								<ClientManagementIcon />
								<Typography variant='body1'>User Database</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/admin/tax-strategies'>
								<TaxStrategiesIcon />
								<Typography variant='body1'>Tax Strategies</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/admin/price-management'>
								<PriceManagementIcon />
								<Typography variant='body1'>Price Management</Typography>
							</SidebarLink>
						</>
					) : (
						<>
							{/* Regular user links */}
							<SidebarLink onClick={closeSidebar} to='/'>
								<DashboardIcon />
								<Typography variant='body1'>Dashboard</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/client-management'>
								<ClientManagementIcon />
								<Typography variant='body1'>Client Management</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/client-dashboard'>
								<ClientDashboardIcon />
								<Typography variant='body1'>Client Dashboard</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/tax-strategies'>
								<TaxStrategiesIcon />
								<Typography variant='body1'>Tax Strategies</Typography>
							</SidebarLink>

							<SidebarLink onClick={closeSidebar} to='/analytics'>
								<AnalyticsIcon />
								<Typography variant='body1'>Analytics</Typography>
							</SidebarLink>

							{userData?.role?.name === 'Owner' && (
								<SidebarLink onClick={closeSidebar} to='/subscription-plan'>
									<SubscriptionPlan />
									<Typography variant='body1'>Subscription Plan</Typography>
								</SidebarLink>
							)}

							<SidebarLink onClick={closeSidebar} to='/ai-chatbot'>
								<Chat />
								<Typography variant='body1'>Ask Agent Lara</Typography>
							</SidebarLink>

							<SidebarExternalLink
								href='https://m.youtube.com/@FortunAI-'
								target='_blank'
								rel='noopener noreferrer'
								onClick={closeSidebar}
							>
								<ResourcesIcon />
								<Typography variant='body1'>Resources</Typography>
							</SidebarExternalLink>

							<SidebarLink onClick={closeSidebar} to='/help'>
								<HelpIcon />
								<Typography variant='body1'>Help</Typography>
							</SidebarLink>
						</>
					)}
				</SidebarLinks>

				{/* Create new widget - only for non-admins */}
				{!isSuperAdminUser ? <CreateNewWidget /> : <EmptyContainer />}

				{/* Sidebar logout button */}
				<LogoutButton />
			</SidebarContent>
		</SidebarWrapper>
	)
}
